import { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { ExerciseInstructions } from "./components/ExerciseInstructions";
import { ExerciseFields } from "./components/ExerciseFields";
import { ExerciseActions } from "./components/ExerciseActions";
import { Exercise } from "./types/ExerciseTypes";
import { isCardioExercise } from "./utils/exerciseUtils";
import { useToast } from "@/components/ui/use-toast";
import { useIsMobile } from "@/hooks/use-mobile";
import { useTranslations } from "@/hooks/useTranslations";
import { ExerciseImage } from "@/components/exercises/ExerciseImage";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

interface ExerciseItemProps {
  exercise: Exercise;
  exerciseIndex: number;
  dayIndex: number;
  totalExercises: number;
  onUpdateExercise: (dayIndex: number, exerciseIndex: number, field: keyof Exercise, value: string | number | string[]) => void;
  onRemoveExercise: (dayIndex: number, exerciseIndex: number) => void;
  onMoveExercise: (dayIndex: number, exerciseIndex: number, direction: 'up' | 'down') => void;
}

export const ExerciseItem = ({
  exercise,
  exerciseIndex,
  dayIndex,
  totalExercises,
  onUpdateExercise,
  onRemoveExercise,
  onMoveExercise
}: ExerciseItemProps) => {
  const { toast } = useToast();
  const isMobile = useIsMobile();
  const { t } = useTranslations("workouts");

  if (!exercise) {
    return <Card className="bg-red-100 p-4 rounded-md dark:bg-red-900/30">
        <CardContent className="p-4">
          {t("exercises.notFound")}
        </CardContent>
      </Card>;
  }

  const isCardio = isCardioExercise(exercise);
  const imageSize = isMobile ? 160 : 200;

  return (
    <Card className="bg-white shadow-sm hover:shadow-md transition-all overflow-hidden dark:bg-gray-800 dark:border-gray-700">
      <CardContent className="p-4">
        <div className="grid grid-cols-1 md:grid-cols-[auto_1fr] gap-x-4 gap-y-2">
          
          <div 
            className="md:col-span-1 row-span-1 md:row-span-2 mx-auto md:mx-0 rounded-lg overflow-hidden border-2 border-slate-200 bg-slate-50 dark:bg-gray-800 dark:border-gray-700"
            style={{ width: `${imageSize}px`, height: `${imageSize}px` }}
          >
            <ExerciseImage
              imagePath={exercise.image_url || exercise.gif_url || ""}
              altText={exercise.name}
              fallbackText={exercise.name}
              className="w-full h-full object-cover"
              isCustomExercise={exercise.isCustom}
            />
          </div>

          <div className="md:col-span-1 flex flex-col gap-3">
            <div>
              <div className="flex items-start justify-between mb-1">
                <Input
                  className="border-none focus-visible:ring-0 p-0 text-lg font-medium h-auto dark:text-white dark:bg-transparent"
                  placeholder={t("newWorkout.exercises.namePlaceholder") || "Nome do exercício"}
                  value={exercise.name}
                  onChange={(e) => onUpdateExercise(dayIndex, exerciseIndex, 'name', e.target.value)}
                />
              </div>
              <div className="flex flex-wrap gap-1.5 text-xs mb-2">
                {exercise.body_part && (
                  <span className="bg-blue-50 text-blue-700 px-2.5 py-0.5 rounded-full dark:bg-blue-900/30 dark:text-blue-300">
                    {exercise.body_part}
                  </span>
                )}
                {exercise.target_muscle && (
                  <span className="bg-green-50 text-green-700 px-2.5 py-0.5 rounded-full dark:bg-green-900/30 dark:text-green-300">
                    {exercise.target_muscle}
                  </span>
                )}
              </div>
            </div>

            <ExerciseFields 
              exercise={exercise} 
              exerciseIndex={exerciseIndex} 
              dayIndex={dayIndex} 
              isCardio={isCardio} 
              onUpdateExercise={onUpdateExercise} 
            />
            
            <ExerciseInstructions 
               instructions={exercise.instructions} 
            />

            <div className="space-y-1">
              <label className="text-xs text-muted-foreground">{t("newWorkout.exercises.notes")}</label>
              <Textarea
                value={exercise.notes || ""}
                onChange={(e) => onUpdateExercise(dayIndex, exerciseIndex, "notes", e.target.value)}
                placeholder=""
                className="min-h-[60px] resize-y bg-white"
              />
            </div>
          </div>
          
          <div className="col-span-full mt-0"> 
            <ExerciseActions 
              exerciseIndex={exerciseIndex} 
              dayIndex={dayIndex} 
              totalExercises={totalExercises} 
              onRemoveExercise={onRemoveExercise} 
              onMoveExercise={onMoveExercise} 
            />
          </div>
          
        </div> 
      </CardContent>
    </Card>
  );
};
